import { AsyncPipe, getCurrencySymbol, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacySnackBarModule, MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatToolbarModule } from '@angular/material/toolbar';
import { OrderDetailService } from '@cutting/main/order/order-detail/order-detail.service';
import { LocalStorageManager } from '@cutting/main/shared/helper/local-storage-manager.helper';
import { fuseAnimations } from '@fuse/animations';
import {
  BazisMaterialHelper,
  CuttingPanelListService,
  CuttingProduct,
  CuttingSettings,
  Estimate,
  EstimateMaterial,
  EstimateOperation,
  LegacyDialogsService,
  MatBaseGroupDiscount,
  NgxLocalizedNumbers,
  OrderStatusAction,
  ProductProcessingMode,
} from 'cloud-shared-lib';
import * as _ from 'lodash';
import { Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, filter, map, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'cutting-estimate',
  templateUrl: './estimate.component.html',
  styleUrls: ['./estimate.component.scss'],
  animations: fuseAnimations,
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    NgTemplateOutlet,
    AsyncPipe,
    NgxLocalizedNumbers,
    MatDividerModule,
    MatIconModule,
    MatToolbarModule,
    MatTooltipModule,
    MatButtonModule,
    MatLegacySnackBarModule,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class EstimateComponent implements OnInit, OnDestroy {
  @Input() materialDiscount: MatBaseGroupDiscount[] = [];
  @Input() operationsDiscount: MatBaseGroupDiscount[] = [];
  @Input() cuttingSettings: CuttingSettings;

  currency: string;
  estimate: Estimate = new Estimate();
  vatPercent = 0;
  vatIncludeInPrice = false;
  unsubscribe$: Subject<void> = new Subject();
  // furniture: CuttingOrderFurniture[] = [];
  products: CuttingProduct[] = [];

  additionalOperationsAction$: Observable<number>;

  get confirmedOrder(): boolean {
    if (!this.orderDetailService.order) {
      return false;
    }

    return (
      this.orderDetailService.onOrderChanged.value.status.action !== OrderStatusAction.Create &&
      this.orderDetailService.onOrderChanged.value.status.action !== OrderStatusAction.CuttingCompleted &&
      this.orderDetailService.onOrderChanged.value.status.action !== OrderStatusAction.Ordering
    );
  }

  get isCuttingCompleted(): boolean {
    return this.orderDetailService.onOrderChanged.value.status.action === OrderStatusAction.CuttingCompleted;
  }

  get singleEstimateList(): boolean {
    return this.cuttingSettings.specificationSettings.singleEstimateList;
  }

  get showItemsPriceBeforeVerified(): boolean {
    return this.cuttingSettings.estimateSettings.showItemsPriceBeforeVerified;
  }

  get showTax(): boolean {
    return this.cuttingSettings.specificationSettings.showTax;
  }

  get countPrecision(): number {
    return this.cuttingSettings.estimateSettings.countPrecision;
  }

  get productMeasure(): string {
    return $localize`шт`;
  }

  get showDiscountPercent(): boolean {
    return this.cuttingSettings.showDiscountPercent;
  }

  get showOnlyMainWork(): boolean {
    return this.cuttingSettings.invoiceServiceInfo.showOnlyMainWork;
  }

  get nameMainWork(): string {
    return this.cuttingSettings.invoiceServiceInfo.nameMainWork;
  }

  hasMaterialsDiscount = false;
  hasOperationsDiscount = false;
  hasAdditionalOperationsDiscount = false;
  hasAdditionalOperationsDescription = false;
  hasAddedAdditionalOperations = false;

  constructor(
    private orderDetailService: OrderDetailService,
    private cuttingPanelListService: CuttingPanelListService,
    private dialogsService: LegacyDialogsService,
    private snackBar: MatSnackBar,
    private destroyRef: DestroyRef
  ) {
    this.currency = getCurrencySymbol(LocalStorageManager.client.currencyCode, 'narrow');

    this.orderDetailService.onEstimateOrderChanged
      .pipe(
        tap((response: Estimate) => {
          this.estimate = response;

          this.hasMaterialsDiscount = this.checkDiscount(this.estimate?.Materials || []);
          this.hasOperationsDiscount = this.checkDiscount(this.estimate?.Operations || []);
          this.hasAdditionalOperationsDiscount = this.checkDiscount(this.estimate?.AdditionalOperations || []);
          this.hasAdditionalOperationsDescription = this.checkDescription(this.estimate?.AdditionalOperations || []);
          this.hasAddedAdditionalOperations = this.checkAddedAdditionalOperations(this.estimate?.Operations || []);
        }),
        concatMap(() => this.orderDetailService.getElasticProducts()),
        tap(
          res =>
            (this.products = res.result
              ? res.result.filter(p => p.processingMode === ProductProcessingMode.PriceList)
              : [])
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
    this.getEstimate();
  }

  ngOnInit(): void {
    this.orderDetailService
      .getVAT()
      .pipe(
        tap(res => {
          this.vatPercent = res.vatPercent;
          this.vatIncludeInPrice = res.vatIncludeInPrice;
        }),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.snackBar.open($localize`Не удалось получить информацию по налоговой ставке`);
          return of({ vatPercent: 0, vatIncludeInPrice: true });
        })
      )
      .subscribe();
  }

  getMaterialName(name: string, article: string): string {
    return BazisMaterialHelper.formatMaterialNameAndArticle(name, article);
  }

  checkDiscount(discounts: EstimateMaterial[] | EstimateOperation[]): boolean {
    return discounts.some(v => v.DiscountPct);
  }

  checkDescription(discounts: EstimateOperation[]): boolean {
    return discounts.some(v => v.Description);
  }

  getEstimate(): void {
    this.orderDetailService.getOrderEstimate();
  }

  getProductsTotalPrice(products: CuttingProduct[]): number {
    if (!products) {
      return 0;
    }

    return products.map(p => p.price * p.count).reduce((acc, val) => acc + val, 0);
  }

  addedAdditionalOperations(operations: EstimateOperation[]): EstimateOperation[] {
    return operations?.filter(o => o.AdditionalGUID) ?? [];
  }

  getPriceOrderWithoutAdditionalOperations(estimate: Estimate): number {
    const priceAddedAdditionalOperations = _.sumBy(this.addedAdditionalOperations(estimate?.Operations), a => {
      return a.Price;
    });

    return estimate?.PriceOrder - priceAddedAdditionalOperations ?? 0;
  }

  getPriceAdditionalOperations(operations: EstimateOperation[]): number {
    const priceAddedAdditionalOperations = _.sumBy(this.addedAdditionalOperations(operations), a => {
      return a.Price;
    });

    return priceAddedAdditionalOperations;
  }

  getDiscountWithAdditionalOperations(operations: EstimateOperation[]): number {
    const discountAddedAdditionalOperations = _.sumBy(this.addedAdditionalOperations(operations), a => {
      return a.Discount;
    });

    return discountAddedAdditionalOperations;
  }

  getDiscountPercentByOnlyCutting(): number {
    const discount =
      this.estimate?.DiscountOrder +
      this.estimate?.DiscountMaterial +
      this.estimate?.DiscountOperation -
      this.getDiscountWithAdditionalOperations(this.estimate?.Operations);

    const totalPrice =
      this.getPriceOrderWithoutAdditionalOperations(this.estimate) + this.getProductsTotalPrice(this.products);

    return _.round((discount / totalPrice) * 100) || 0;
  }

  addAdditionalOperation(operation: EstimateOperation): void {
    const unitPrice = operation.Price / operation.Count;

    const adder$ = !operation.NotVariableQuantity
      ? this.dialogsService
          .promptNumber(
            $localize`Добавление доп. услуги`,
            operation.Description,
            operation.Count,
            $localize`Количество`,
            true,
            [Validators.required, Validators.min(0)]
          )
          .pipe(
            filter(r => r.result),
            map(r => +r.value)
          )
      : of(operation.Count);

    this.additionalOperationsAction$ = adder$.pipe(
      concatMap(count =>
        this.orderDetailService.addAdditionalOperation(operation.AdditionalGUID, count).pipe(map(() => count))
      ),
      catchError(err => {
        this.snackBar.open($localize`Не удалось добавить дополнительную услугу`);
        return of(err);
      }),
      tap(count => {
        operation.Count = count;
        operation.Price = count * unitPrice;
        this.estimate.Operations.push(operation);

        const additionalOperationIndex = this.estimate.AdditionalOperations.indexOf(operation);
        this.estimate.AdditionalOperations.splice(additionalOperationIndex, 1);

        this.hasOperationsDiscount = this.checkDiscount(this.estimate?.Operations || []);
        this.hasAdditionalOperationsDiscount = this.checkDiscount(this.estimate?.AdditionalOperations || []);
        this.hasAdditionalOperationsDescription = this.checkDescription(this.estimate?.AdditionalOperations || []);
        this.hasAddedAdditionalOperations = this.checkAddedAdditionalOperations(this.estimate?.Operations || []);

        this.orderDetailService.getOrder();
        this.orderDetailService.getOrderEstimate();
      })
    );
  }

  removeAdditionalOperation(operation: EstimateOperation): void {
    this.additionalOperationsAction$ = this.orderDetailService.removeAdditionalOperation(operation.AdditionalGUID).pipe(
      tap(() => {
        const operationIndex = this.estimate.Operations.indexOf(operation);
        this.estimate.Operations.splice(operationIndex, 1);

        this.estimate.AdditionalOperations.unshift(operation);

        this.hasOperationsDiscount = this.checkDiscount(this.estimate?.Operations || []);
        this.hasAdditionalOperationsDiscount = this.checkDiscount(this.estimate?.AdditionalOperations || []);
        this.hasAdditionalOperationsDescription = this.checkDescription(this.estimate?.AdditionalOperations || []);
        this.hasAddedAdditionalOperations = this.checkAddedAdditionalOperations(this.estimate?.Operations || []);

        this.orderDetailService.getOrder();
        this.orderDetailService.getOrderEstimate();
      }),
      catchError(err => {
        this.snackBar.open($localize`Не удалось удалить дополнительную услугу`);
        return of(err);
      })
    );
  }

  checkAddedAdditionalOperations(operations: EstimateOperation[]): boolean {
    return operations.some(o => o.AdditionalGUID);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
